import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, ScanLine, CheckCircle2, XCircle, Camera } from 'lucide-react';
import CameraScanner from './CameraScanner';

/**
 * ScannerInput — capture EAN-13 barcodes from either:
 *   • a USB/Bluetooth HID scanner (types the digits + Enter), OR
 *   • the device camera (iPad Safari 17+ or any Chromium browser).
 *
 * Fires `onScan(code)` when a code is captured. The scanner stays
 * focused between scans; the camera panel closes automatically after
 * a successful decode.
 */
export default function ScannerInput({
  onScan,
  disabled = false,
  autoFocus = true,
  placeholder = 'Scan or type barcode…',
  status = 'ready', // 'ready' | 'scanning' | 'success' | 'error'
  statusMessage = null,
  testId = 'scanner-input',
  allowCamera = true,
}) {
  const inputRef = useRef(null);
  const [value, setValue] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);

  useEffect(() => {
    if (autoFocus && !disabled && !cameraOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus, disabled, cameraOpen]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const code = (value || '').trim();
      if (code.length >= 8) {
        onScan?.(code);
        setValue('');
      }
    }
  };

  const handleCameraDetect = (code) => {
    setCameraOpen(false);
    onScan?.((code || '').trim());
  };

  const StatusPill = () => {
    if (status === 'scanning') {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2.5 py-1">
          <Loader2 size={12} className="animate-spin" /> Scanning
        </span>
      );
    }
    if (status === 'success') {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
          <CheckCircle2 size={12} /> {statusMessage || 'Match'}
        </span>
      );
    }
    if (status === 'error') {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2.5 py-1">
          <XCircle size={12} /> {statusMessage || 'Not found'}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1">
        <ScanLine size={12} /> Ready
      </span>
    );
  };

  return (
    <div className="flex flex-col gap-2" data-testid={`${testId}-wrapper`}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <ScanLine
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <Input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled || cameraOpen}
            placeholder={placeholder}
            className="pl-10 h-12 text-lg font-mono tracking-wider"
            inputMode="numeric"
            autoComplete="off"
            data-testid={testId}
          />
        </div>
        {allowCamera && (
          <Button
            type="button"
            variant={cameraOpen ? 'default' : 'outline'}
            size="sm"
            className="h-12"
            onClick={() => setCameraOpen((x) => !x)}
            data-testid={`${testId}-camera-btn`}
          >
            <Camera size={16} className="mr-1" />
            {cameraOpen ? 'Close' : 'Camera'}
          </Button>
        )}
        <StatusPill />
      </div>
      {cameraOpen && (
        <CameraScanner
          onDetect={handleCameraDetect}
          testId={`${testId}-camera`}
        />
      )}
      {!cameraOpen && (
        <p className="text-xs text-slate-500">
          Use a handheld scanner (types the code + Enter) or tap{' '}
          <strong>Camera</strong> to scan with the iPad camera.
        </p>
      )}
    </div>
  );
}
